import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort } from '@syncfusion/ej2-react-grids';
import { useWizardContext } from '../../contexts/useWizardContext';

/** One row per dough batch (same shape as the new batches review). */
interface AusbackenBatchRow {
  sequenceNumber?: number;
  productName?: string;
  campaignId?: string;
  batchSequenceNumber?: number;
  quantity?: number;
  doughQuantity?: number;
}

interface LongTermBakeOutTabProps {
  gridHeightPx?: number;
}

const LongTermBakeOutTab: React.FC<LongTermBakeOutTabProps> = ({ gridHeightPx = 320 }) => {
  const { t } = useTranslation(['common']);
  const { followUpOrders } = useWizardContext();
  // useMemo stabilizes the array reference so that Syncfusion does not trigger an unnecessary re-init
  const data: AusbackenBatchRow[] = useMemo(
    () => Array.isArray(followUpOrders) ? [...followUpOrders] : [],
    [followUpOrders]
  );

  const gridColumns = [
    { field: 'sequenceNumber', headerText: t('productionSchedule.wizard.step4.sequenceNumber', 'Sequenz-Nr.'), width: 100, textAlign: 'Right' as const },
    { field: 'productName', headerText: t('productionSchedule.wizard.step4.product', 'Produkt'), width: 200 },
    { field: 'campaignId', headerText: t('productionSchedule.wizard.step4.campaignId', 'Kampagne'), width: 150 },
    { field: 'batchSequenceNumber', headerText: t('productionSchedule.wizard.step4.batchSequenceNumber', 'Charge'), width: 80, textAlign: 'Right' as const },
    { field: 'quantity', headerText: t('productionSchedule.wizard.step4.quantity', 'Stückzahl'), width: 100, textAlign: 'Right' as const, format: 'n0' },
    { field: 'doughQuantity', headerText: t('productionSchedule.wizard.step4.doughQuantity', 'Teigmenge (kg)'), width: 120, textAlign: 'Right' as const, format: 'n2' }
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 mb-2">
        {t('productionSchedule.wizard.step5.description', 'Vorschau: Aufträge mit Langzeit geführten Produkten, deren Nachfolger-Vorgänge (Formen, Ausbacken) im nächsten Planungslauf eingeplant werden.')}
      </p>
      {data.length === 0 ? (
        <div className="border border-gray-200 rounded-lg p-8 text-center bg-gray-50">
          <p className="text-gray-600">
            {t('productionSchedule.wizard.step5.noLongTermProducts', 'Keine Langzeit geführten Produkte mit Ausbacken im nächsten Lauf.')}
          </p>
        </div>
      ) : (
        <GridComponent
          dataSource={data}
          height={gridHeightPx}
          allowPaging={false}
          allowSorting={true}
          locale="de-DE"
        >
          <ColumnsDirective>
            {gridColumns.map((col, idx) => (
              <ColumnDirective
                key={idx}
                field={col.field}
                headerText={col.headerText}
                width={col.width}
                textAlign={(col as { textAlign?: 'Right' | 'Left' }).textAlign}
                format={(col as { format?: string }).format}
              />
            ))}
          </ColumnsDirective>
          <Inject services={[Sort]} />
        </GridComponent>
      )}
    </div>
  );
};

export default LongTermBakeOutTab;
